import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/feature/Navigation';
import Footer from '@/pages/home/components/Footer';
import SEOHead from '@/components/SEOHead';

interface FaqItem {
  q: string;
  a: string;
}

interface FaqCategory {
  id: string;
  icon: string;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  items: FaqItem[];
}

const categories: FaqCategory[] = [
  {
    id: 'safety',
    icon: 'ri-shield-check-line',
    label: 'Safety & Quality',
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/25',
    items: [
      {
        q: 'Is it safe to get dental treatment in Turkey?',
        a: 'Absolutely. Turkey is one of the world\'s leading medical tourism destinations, with clinics held to the same international standards as top hospitals in the UK, US, and Europe. Our partner clinics are JCI-accredited — the gold standard in global healthcare — and all our dentists are board-certified specialists with 10+ years of experience. On top of that, every Plexus Alliance patient has an independent medical supervisor overseeing their treatment, which is something most clinics anywhere in the world don\'t offer.',
      },
      {
        q: 'What is JCI accreditation and why does it matter?',
        a: 'JCI (Joint Commission International) is the most rigorous international healthcare accreditation body in the world. Fewer than 1% of hospitals and clinics globally hold this certification. It means the clinic meets strict standards for patient safety, infection control, staff qualifications, and clinical outcomes. When you see JCI-accredited, you know the facility has been independently verified to operate at the highest level.',
      },
      {
        q: 'What does "independent supervision" actually mean?',
        a: 'It means a qualified medical professional — who is not your treating dentist and has no financial stake in your case — reviews your treatment plan before it starts, monitors progress during your stay, and signs off on the final result. This is our core differentiator. It\'s like having a second opinion built into every step of your treatment, not just at the end.',
      },
      {
        q: 'What if something goes wrong during treatment?',
        a: 'Our supervisors are on-site and can intervene immediately if anything is not right. All our partner clinics carry full medical liability insurance. In the rare event of a complication, we coordinate everything — additional treatment, specialist referrals, and follow-up care — at no extra cost to you.',
      },
      {
        q: 'Are the materials and implants used the same quality as in my home country?',
        a: 'Yes — and often better. We use internationally recognised brands like Straumann, Nobel Biocare, and Osstem for implants, and top-tier ceramic materials for veneers and crowns. The same brands used in London, New York, and Sydney. The difference is the cost of labour and clinic overheads in Turkey, not the quality of materials.',
      },
    ],
  },
  {
    id: 'costs',
    icon: 'ri-money-pound-circle-line',
    label: 'Costs & Savings',
    color: 'text-amber-300',
    bgColor: 'bg-amber-300/10',
    borderColor: 'border-amber-300/25',
    items: [
      {
        q: 'How much can I actually save compared to my home country?',
        a: 'Typically 50–75% — even after flights, hotel, and transfers. A single dental implant that costs £2,500–£3,500 in the UK runs £600–£900 all-in through us. A full smile makeover (8–10 veneers) that costs £8,000–£15,000 in London is often £2,500–£4,500 with us. We\'ll give you an exact comparison quote once we review your case.',
      },
      {
        q: 'Are there any hidden costs I should know about?',
        a: 'No. We give you a full all-inclusive quote before you commit to anything. This covers your treatment, airport transfers, hotel accommodation near the clinic, and your dedicated coordinator. The only things not included are your flights and personal spending. No surprise invoices, no add-ons at the clinic.',
      },
      {
        q: 'Do I pay upfront or when I arrive?',
        a: 'We typically ask for a small deposit to confirm your booking and hold your appointment slot. The remainder is paid on arrival in Istanbul. We\'ll walk you through the payment schedule clearly before you book.',
      },
      {
        q: 'Does the price include the hotel and transfers?',
        a: 'Yes. Our packages include private airport pickup and drop-off, accommodation at a partner hotel within minutes of the clinic, and all clinic transfers during your stay. You won\'t need to arrange or pay for any of this separately.',
      },
      {
        q: 'What if I need more treatment than originally quoted?',
        a: 'If the dentist identifies additional work needed during your consultation (which sometimes happens with X-rays), we\'ll discuss it with you transparently before proceeding. You\'re never pressured into anything. Any changes to the treatment plan are agreed with you first, with updated pricing.',
      },
    ],
  },
  {
    id: 'travel',
    icon: 'ri-flight-takeoff-line',
    label: 'Travel & Logistics',
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/25',
    items: [
      {
        q: 'How long do I need to stay in Istanbul?',
        a: 'It depends on your treatment. Teeth whitening or a single crown can be done in 2–3 days. Veneers typically take 5–7 days. Dental implants require two trips — the first for implant placement (3–5 days), then a return visit 3–6 months later for the final crown. We\'ll give you an exact timeline when we review your case.',
      },
      {
        q: 'Do I need a visa to visit Turkey?',
        a: 'Citizens of most Western countries (UK, EU, US, Canada, Australia) can enter Turkey visa-free or obtain an e-Visa online in minutes for around $50. We\'ll confirm the requirements for your specific nationality when you book. The process is straightforward and we can guide you through it.',
      },
      {
        q: 'What airport should I fly into?',
        a: 'Istanbul has two international airports — Istanbul Airport (IST) and Sabiha Gökçen (SAW). Both work well. Istanbul Airport is larger with more direct routes from Europe and the US. We arrange private transfers from whichever airport you land at.',
      },
      {
        q: 'Is Istanbul safe to visit as a tourist?',
        a: 'Istanbul is one of the world\'s most visited cities, welcoming over 15 million tourists a year. The areas where our partner clinics and hotels are located are safe, modern, and well-connected. Our coordinators are with you throughout your stay, so you\'re never navigating the city alone.',
      },
      {
        q: 'Can I bring a companion with me?',
        a: 'Absolutely — and many patients do. We can arrange accommodation for a companion and suggest things to do in Istanbul while you\'re in treatment. Istanbul is a stunning city with incredible food, history, and culture. Many patients turn their dental trip into a mini-holiday.',
      },
      {
        q: 'What should I do to prepare before I travel?',
        a: 'We\'ll send you a full pre-travel checklist once you book. Generally: arrange travel insurance, bring any existing dental X-rays or records, avoid alcohol 48 hours before treatment, and let us know about any medications you\'re taking. We handle everything else.',
      },
    ],
  },
  {
    id: 'treatments',
    icon: 'ri-tooth-line',
    label: 'Treatments',
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/25',
    items: [
      {
        q: 'What treatments do you offer?',
        a: 'We cover the full range of cosmetic and restorative dentistry: dental implants (single, multiple, All-on-4, All-on-6), porcelain veneers, smile makeovers, crowns and bridges, teeth whitening, Invisalign and orthodontics, gum contouring, and full-mouth rehabilitation. If you\'re not sure what you need, we\'ll assess your case and recommend the right treatment.',
      },
      {
        q: 'How do I know which treatment is right for me?',
        a: 'Send us your photos and any existing X-rays — we\'ll have a specialist review your case for free and give you a clear recommendation. No obligation, no pressure. We\'d rather you make the right decision than the fast one.',
      },
      {
        q: 'Are dental implants done in one visit or two?',
        a: 'Implants are typically a two-stage process. In the first visit, the implant post is placed into the jawbone (3–5 days in Istanbul). After 3–6 months of healing at home, you return for the final crown fitting (2–3 days). Some cases qualify for same-day implants — we\'ll let you know if you\'re a candidate.',
      },
      {
        q: 'How long do veneers last?',
        a: 'High-quality porcelain veneers last 15–20 years with proper care. We use premium ceramic materials and our dentists are specialists in cosmetic work. We\'ll also give you a full aftercare guide to maximise the lifespan of your veneers.',
      },
      {
        q: 'Will my treatment look natural?',
        a: 'Yes — this is something our cosmetic dentists take very seriously. We use shade-matching technology and work closely with you on the shape, size, and colour of your new teeth. You\'ll see a digital preview before any permanent work is done, so you can approve the look first.',
      },
    ],
  },
  {
    id: 'aftercare',
    icon: 'ri-heart-pulse-line',
    label: 'Aftercare & Follow-up',
    color: 'text-amber-300',
    bgColor: 'bg-amber-300/10',
    borderColor: 'border-amber-300/25',
    items: [
      {
        q: 'What aftercare support do I get after I go home?',
        a: 'You\'ll have a direct WhatsApp line to your coordinator for any questions or concerns. Your treating dentist will schedule follow-up video consultations to monitor your recovery. We also send your full treatment records to you and your local dentist so they\'re fully informed. If anything needs attention, we coordinate it.',
      },
      {
        q: 'What if I need a revision after I\'m back home?',
        a: 'If a revision is needed due to the treatment itself (not external damage), we coordinate it at no extra cost. This may involve a return visit to Istanbul or, in minor cases, working with a local dentist whose fees we cover. We stand behind our work.',
      },
      {
        q: 'Can I eat and drink normally after treatment?',
        a: 'It depends on the treatment. After implant surgery, you\'ll need soft foods for a few days. After veneers, you can eat normally but should avoid very hard foods. We give you a detailed aftercare guide specific to your treatment before you leave Istanbul.',
      },
      {
        q: 'Do I need to see a dentist when I get home?',
        a: 'We recommend a check-up with your local dentist 6–8 weeks after returning home. We\'ll provide them with your full treatment records so they\'re up to speed. Regular dental hygiene appointments are important for maintaining your results long-term.',
      },
    ],
  },
];

const siteUrl = import.meta.env.VITE_SITE_URL || '';

const allFaqs = categories.flatMap((c) => c.items);

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: allFaqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState<string>('safety');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const current = categories.find((c) => c.id === activeCategory) ?? categories[0];

  const handleCategoryChange = (id: string) => {
    setActiveCategory(id);
    setOpenIndex(0);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="FAQ — Dental Tourism Turkey | Plexus Alliance Istanbul"
        description="Answers to the most common questions about dental tourism in Turkey — safety, costs, travel logistics, treatments, and aftercare. Plexus Alliance, Istanbul."
        keywords="dental tourism Turkey FAQ, dental implants Turkey cost, safe dental treatment Istanbul, dental veneers Turkey questions"
        canonical={`${siteUrl}/faq`}
        schema={schema}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative pt-36 pb-20 md:pt-48 md:pb-28 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://public.readdy.ai/ai/img_res/2ffab3c060af76e21620cd2f6878ccf5.jpg')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/70 to-[#0a0a0a]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-10 h-px bg-amber-400/50" />
            <span
              className="text-amber-400/80 text-xs font-medium tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Got Questions?
            </span>
            <div className="w-10 h-px bg-amber-400/50" />
          </div>
          <h1
            className="text-4xl md:text-7xl font-normal text-white leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.02em' }}
          >
            Frequently Asked <em className="text-amber-300 not-italic">Questions</em>
          </h1>
          <p
            className="text-white/50 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Everything you need to know about dental tourism in Istanbul — safety, costs, travel, and what to expect.
          </p>

          {/* Stats strip */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-12">
            {[
              { value: '2,400+', label: 'Patients treated' },
              { value: '50–75%', label: 'Average savings' },
              { value: '5–10', label: 'Days in Istanbul' },
              { value: '100%', label: 'Supervised care' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p
                  className="text-2xl font-semibold text-amber-400 mb-1"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {stat.value}
                </p>
                <p
                  className="text-white/30 text-xs tracking-widest uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Body */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-14">

            {/* Sidebar — category tabs */}
            <aside className="w-full lg:w-64 flex-shrink-0">
              <div className="lg:sticky lg:top-28 space-y-1">
                <p
                  className="text-[10px] text-white/25 tracking-[0.25em] uppercase mb-4 px-1"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Browse by topic
                </p>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-300 cursor-pointer rounded-lg group ${
                      activeCategory === cat.id
                        ? 'bg-amber-400/10 border border-amber-400/25'
                        : 'border border-transparent hover:border-white/8 hover:bg-white/3'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-md flex-shrink-0 transition-colors duration-300 ${
                        activeCategory === cat.id ? cat.bgColor : 'bg-white/5'
                      }`}
                    >
                      <i
                        className={`${cat.icon} text-sm transition-colors duration-300 ${
                          activeCategory === cat.id ? cat.color : 'text-white/30 group-hover:text-white/60'
                        }`}
                      ></i>
                    </div>
                    <div>
                      <p
                        className={`text-sm font-medium transition-colors duration-300 ${
                          activeCategory === cat.id ? 'text-white' : 'text-white/40 group-hover:text-white/70'
                        }`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {cat.label}
                      </p>
                      <p
                        className="text-xs text-white/20 mt-0.5"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {cat.items.length} questions
                      </p>
                    </div>
                    {activeCategory === cat.id && (
                      <i className={`ri-arrow-right-s-line ml-auto ${cat.color} text-base`}></i>
                    )}
                  </button>
                ))}

                {/* Contact card */}
                <div
                  className="mt-6 p-5 rounded-xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(251,191,36,0.08) 0%, rgba(251,191,36,0.03) 100%)',
                    border: '1px solid rgba(251,191,36,0.15)',
                  }}
                >
                  <p
                    className="text-white/70 text-sm font-medium mb-1"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Still not sure?
                  </p>
                  <p
                    className="text-white/30 text-xs leading-relaxed mb-4"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    Ask us directly — we respond within a few hours.
                  </p>
                  <a
                    href="https://wa.me/message/OAKDCTBVIML6F1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs font-semibold text-black bg-amber-400 hover:bg-amber-300 px-4 py-2.5 rounded-md transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <i className="ri-whatsapp-line text-sm"></i>
                    Chat on WhatsApp
                  </a>
                </div>
              </div>
            </aside>

            {/* Main accordion */}
            <div className="flex-1 min-w-0">
              {/* Category header */}
              <div className="flex items-center gap-4 mb-8 pb-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${current.bgColor} border ${current.borderColor}`}>
                  <i className={`${current.icon} text-lg ${current.color}`}></i>
                </div>
                <div>
                  <h2
                    className="text-2xl md:text-3xl font-normal text-white"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {current.label}
                  </h2>
                  <p
                    className="text-white/30 text-sm mt-0.5"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {current.items.length} questions answered
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {current.items.map((faq, i) => (
                  <div
                    key={`${current.id}-${i}`}
                    className={`rounded-xl overflow-hidden transition-all duration-300 ${
                      openIndex === i
                        ? `border ${current.borderColor}`
                        : 'border border-white/6 hover:border-white/12'
                    }`}
                    style={{
                      backgroundColor: openIndex === i ? 'rgba(251,191,36,0.03)' : 'rgba(255,255,255,0.02)',
                    }}
                  >
                    <button
                      onClick={() => setOpenIndex(openIndex === i ? null : i)}
                      className="w-full flex items-start justify-between px-6 py-5 text-left cursor-pointer group gap-4"
                    >
                      <div className="flex items-start gap-4">
                        <span
                          className={`text-xs font-bold mt-0.5 flex-shrink-0 transition-colors duration-300 ${
                            openIndex === i ? current.color : 'text-white/20'
                          }`}
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span
                          className={`text-sm md:text-base font-medium transition-colors duration-200 leading-snug ${
                            openIndex === i ? 'text-white' : 'text-white/65 group-hover:text-white/90'
                          }`}
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {faq.q}
                        </span>
                      </div>
                      <div
                        className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border transition-all duration-300 mt-0.5 ${
                          openIndex === i
                            ? `${current.bgColor} ${current.borderColor}`
                            : 'border-white/10 group-hover:border-white/20'
                        }`}
                      >
                        <i
                          className={`text-sm transition-all duration-300 ${
                            openIndex === i
                              ? `ri-subtract-line ${current.color}`
                              : 'ri-add-line text-white/30 group-hover:text-white/60'
                          }`}
                        ></i>
                      </div>
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-500 ${
                        openIndex === i ? 'max-h-96' : 'max-h-0'
                      }`}
                    >
                      <div className="px-6 pb-6 pl-16">
                        <p
                          className="text-white/45 text-sm leading-relaxed"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {faq.a}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-14 md:py-20 bg-[#0d0d0d]" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <p
              className="text-white/25 text-xs tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Why patients trust Plexus Alliance
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-xl overflow-hidden">
            {[
              { icon: 'ri-shield-check-line', title: 'JCI-Accredited Clinics', desc: 'The same standard as top hospitals worldwide' },
              { icon: 'ri-eye-line', title: 'Independent Supervision', desc: 'A second expert reviews every treatment plan' },
              { icon: 'ri-translate-2', title: 'Multilingual Support', desc: 'We speak your language throughout your journey' },
              { icon: 'ri-heart-pulse-line', title: 'Lifetime Aftercare', desc: 'We stay with you long after you go home' },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#111111] p-6 md:p-8 text-center hover:bg-[#161616] transition-colors duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-400/10 mx-auto mb-4">
                  <i className={`${item.icon} text-lg text-amber-400`}></i>
                </div>
                <p
                  className="text-white/80 text-sm font-semibold mb-2"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {item.title}
                </p>
                <p
                  className="text-white/30 text-xs leading-relaxed"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://readdy.ai/api/search-image?query=Istanbul%20Turkey%20Bosphorus%20bridge%20night%20lights%20reflection%20water%20dark%20moody%20cinematic%20premium%20travel%20photography%20wide%20angle%20editorial%20style%20golden%20glow&width=1400&height=600&seq=faq-cta-bg&orientation=landscape')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/92 via-black/78 to-black/92" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-8 h-px bg-amber-400/40" />
            <span
              className="text-amber-400/70 text-xs tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Ready to start?
            </span>
            <div className="w-8 h-px bg-amber-400/40" />
          </div>
          <h2
            className="text-3xl md:text-6xl font-normal text-white mb-6 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Your questions answered.<br />
            <em className="text-amber-300 not-italic">Now take the first step.</em>
          </h2>
          <p
            className="text-white/40 text-lg mb-10 max-w-xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Book a free consultation — no commitment, no pressure. Just a conversation about what\'s possible for you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/book-treatment"
              className="flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-10 py-4 font-semibold text-sm tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap rounded-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Book Free Consultation
              <i className="ri-arrow-right-line"></i>
            </Link>
            <a
              href="https://wa.me/message/OAKDCTBVIML6F1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 border border-white/20 hover:border-amber-400/40 text-white/60 hover:text-amber-400 px-10 py-4 font-medium text-sm tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap rounded-sm"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <i className="ri-whatsapp-line"></i>
              Ask on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer onBookClick={() => window.REACT_APP_NAVIGATE('/book-treatment')} />
    </div>
  );
}
